/**
 * FormDatePicker
 *
 * MUI X's DatePicker as the leave and arrêt forms use it: a small, full-width field in the
 * FormDialog input style, a calendar popup, and the app's language (French or English) for
 * the calendar and the DD/MM/YYYY format.
 *
 * Speaks `YYYY-MM-DD` strings — what those forms and the API already hold — so it replaces an
 * <input type="date"> without the caller converting anything. An empty field is "".
 *
 * `enableAccessibleFieldDOMStructure={false}` keeps the classic TextField input underneath, so
 * `formFieldSx` (which targets `.MuiOutlinedInput-root`) styles it like every other field.
 */
import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { frFR, enUS } from "@mui/x-date-pickers/locales";
import { formFieldSx } from "../popups/FormDialog";

const ISO = "YYYY-MM-DD";

const toMoment = (value) => {
  if (!value) return null;
  const date = moment(value, ISO, true);
  return date.isValid() ? date : null;
};

/**
 * @param {string} props.value - `YYYY-MM-DD`, or "" for no date
 * @param {Function} props.onChange - called with the new `YYYY-MM-DD`, or "" once cleared
 * @param {string} [props.minDate] - `YYYY-MM-DD`; earlier days are disabled
 * @param {string} [props.maxDate] - `YYYY-MM-DD`; later days are disabled
 * @param {boolean} [props.error]
 * @param {React.ReactNode} [props.helperText]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.clearable] - a clear (×) button, for optional dates only; a required
 *        date has nothing sensible to clear to
 * @param {Object} [props.sx] - merged over the form field style
 */
export default function FormDatePicker({ value, onChange, minDate, maxDate, error, helperText, disabled, clearable, sx }) {
  const lang = useSelector((state) => state.settings.lang);
  const isFrench = lang !== "en";
  const localeText = (isFrench ? frFR : enUS).components.MuiLocalizationProvider.defaultProps.localeText;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={isFrench ? "fr" : "en"} localeText={localeText}>
      <DatePicker
        value={toMoment(value)}
        // A half-typed date is invalid until it is complete: it is not reported, so the
        // form never wipes what is being typed. Clearing the field reports "".
        onChange={(date) => {
          if (!date) onChange("");
          else if (date.isValid()) onChange(date.format(ISO));
        }}
        minDate={toMoment(minDate) || undefined}
        maxDate={toMoment(maxDate) || undefined}
        format="DD/MM/YYYY"
        disabled={disabled}
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            error,
            helperText,
            sx: { ...formFieldSx, ...sx },
          },
          field: { clearable: !!clearable && !disabled },
        }}
      />
    </LocalizationProvider>
  );
}
